import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { z } from 'zod'

import { SupportedLanguages } from '@/config/i18n'
import { useFetchMenuListQuery } from '@/ducks/menu/api/menu.api'
import { selectMenuFilters } from '@/ducks/menu/selectors/menu.selectors'
import { setMenuFilters } from '@/ducks/menu/slices/menu.slice'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'

const formSchema = z.object({
  status: z
    .union([z.literal('all'), z.literal('unavailable'), z.literal('available')])
    .nullish(),
  mainCategories: z.array(z.string()).nullish()
})
type FormData = z.infer<typeof formSchema>

const filtersDefaultValues = {
  status: 'all' as const,
  mainCategories: []
}

const useMenuFilterForm = (shouldResetForm: boolean,selectedBranchId?: string) => {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()

  const language = (i18n.resolvedLanguage || 'en') as SupportedLanguages
  const storeId = useSelector(selectStoreId) || ''

  const { mainCategories } = useFetchMenuListQuery(
    {
      store_id: selectedBranchId ? selectedBranchId : storeId,
    },
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        mainCategories:
          data?.menu.mainCategories.map(({ name, _id }) => ({
            name: name[language],
            id: _id
          })) ?? []
      })
    }
  )

  const filters = useSelector(selectMenuFilters)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: filtersDefaultValues
  })
  const { setValue, handleSubmit, formState, getValues } = form
  const { reset, control } = form

  const shouldDisableSubmit = !formState.isDirty

  useEffect(
    function resetDirtyStatus() {
      if (shouldResetForm && formState.isSubmitted) reset(getValues())
    },
    [formState.isSubmitted, getValues, reset, shouldResetForm]
  )

  useEffect(
    function syncFormWithStore() {
      reset({ ...filtersDefaultValues, ...filters })
    },
    [filters, reset, shouldResetForm]
  )

  const onReset = () => {
    Object.entries(filtersDefaultValues).forEach(([key, value]) =>
      setValue(key as keyof typeof filtersDefaultValues, value, {
        shouldDirty: true
      })
    )
    dispatch(setMenuFilters({ ...filters, ...getValues() }))
  }
  const onSubmit = useMemo(
    () =>
      handleSubmit((values: FormData) => {
        dispatch(setMenuFilters({ ...filters, ...values }))
      }),
    [dispatch, filters, handleSubmit]
  )

  return {
    shouldDisableSubmit,
    onReset,
    onSubmit,
    control,
    form,
    mainCategories
  } as const
}

export { useMenuFilterForm }
